/* eslint-disable camelcase */
import React, { useState } from "react"
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material"
import ViewListIcon from "@mui/icons-material/ViewList" // Icono para Lotes
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered" // Icono para Series
import { showWarning } from "../../../../api"

// Importación de los 4 Modales Transaccionales que construiremos a continuación
import ModalLotesIngreso from "./ModalLotesIngreso"
import ModalLotesEgreso from "./ModalLotesEgreso"
import ModalSeriesIngreso from "./ModalSeriesIngreso"
import ModalSeriesEgreso from "./ModalSeriesEgreso"

const DetalleArticulosInvGrid = ({ detalle, setFormData, tratipo, bodcodigo }) => {
  // Estado para controlar qué fila está activa y qué modal se está mostrando
  const [activeRowIndex, setActiveRowIndex] = useState(null)
  const [modalType, setModalType] = useState("") // 'LOTES' o 'SERIES'

  // ==========================================================================
  // MANEJADOR DE CANTIDADES
  // ==========================================================================
  const handleCantidadChange = (index, value) => {
    let val = parseFloat(value) || 0

    // Validación defensiva: No puede ser negativo
    if (val < 0) val = 0

    // Validación de negocio: No puede afectar más de lo que está pendiente en la Solicitud
    if (val > detalle[index].cant_pendiente) {
      showWarning(
        `La cantidad a afectar no puede ser mayor a la cantidad pendiente (${detalle[index].cant_pendiente}).`,
      )
      val = detalle[index].cant_pendiente
    }

    setFormData((prev) => {
      const nuevoDetalle = [...prev.detalle]
      // Al cambiar la cantidad, limpiamos las asignaciones de lotes/series por seguridad
      if (nuevoDetalle[index].cant_afectar !== val) {
        nuevoDetalle[index].lotes = []
        nuevoDetalle[index].series = []
      }
      nuevoDetalle[index].cant_afectar = val
      return { ...prev, detalle: nuevoDetalle }
    })
  }

  // ==========================================================================
  // MANEJADOR DE APERTURA DE MODALES
  // ==========================================================================
  const handleOpenModal = (index, type) => {
    const row = detalle[index]

    // Regla de Negocio: Debe tener una cantidad antes de asignar lotes/series
    if (row.cant_afectar <= 0) {
      return showWarning(
        `Primero digite la 'Cantidad a Afectar' en el artículo ${row.artcodigo} antes de asignar ${type}.`,
      )
    }

    setActiveRowIndex(index)
    setModalType(type)
  }

  const handleCloseModal = () => {
    setActiveRowIndex(null)
    setModalType("")
  }

  // ==========================================================================
  // MANEJADORES PARA GUARDAR LA INFORMACIÓN DEL MODAL EN LA GRILLA
  // ==========================================================================
  const handleSaveLotes = (lotes) => {
    setFormData((prev) => {
      const nuevoDetalle = [...prev.detalle]
      nuevoDetalle[activeRowIndex].lotes = lotes
      return { ...prev, detalle: nuevoDetalle }
    })
    handleCloseModal()
  }

  const handleSaveSeries = (series) => {
    setFormData((prev) => {
      const nuevoDetalle = [...prev.detalle]
      nuevoDetalle[activeRowIndex].series = series
      return { ...prev, detalle: nuevoDetalle }
    })
    handleCloseModal()
  }

  // ==========================================================================
  // VARIABLES CONDICIONALES PARA RENDERIZAR EL MODAL CORRECTO
  // ==========================================================================
  const currentRow = activeRowIndex !== null ? detalle[activeRowIndex] : null

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#f5f7fa" }}>
            <TableRow>
              <TableCell width="5%">#</TableCell>
              <TableCell width="35%">Artículo (Código y Descripción)</TableCell>
              <TableCell width="12%" align="center">
                Cant. Solicitada
              </TableCell>
              <TableCell width="12%" align="center">
                Cant. Procesada
              </TableCell>
              <TableCell width="12%" align="center">
                Cant. Pendiente
              </TableCell>
              <TableCell width="12%" align="center">
                Cant. a Afectar
              </TableCell>
              <TableCell width="12%" align="center">
                Lotes / Series
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detalle.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  No hay artículos en el detalle de la solicitud.
                </TableCell>
              </TableRow>
            ) : (
              detalle.map((row, index) => {
                const procesada = row.sgacanrec + row.sgacanegr

                // Banderas de estado visual de botones (Rojo = Faltan datos, Verde = Completado)
                const lotesCompletos =
                  row.lotes &&
                  row.lotes.reduce((acc, curr) => acc + parseFloat(curr.cantidad || 0), 0) ===
                    parseFloat(row.cant_afectar)

                const seriesCompletas = row.series && row.series.length === parseFloat(row.cant_afectar)

                return (
                  <TableRow key={index} sx={{ backgroundColor: row.cant_pendiente === 0 ? "#f0f0f0" : "inherit" }}>
                    <TableCell>{index + 1}</TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {row.artcodigo}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {row.artdescri}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">{row.sgacansol}</TableCell>

                    <TableCell align="center">
                      <Typography variant="body2" color="secondary" fontWeight="bold">
                        {procesada}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Typography variant="body2" color="primary" fontWeight="bold">
                        {row.cant_pendiente}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <TextField
                        type="number"
                        variant="outlined"
                        size="small"
                        inputProps={{ min: 0, max: row.cant_pendiente, step: "0.01" }}
                        value={row.cant_afectar === 0 ? "" : row.cant_afectar}
                        onChange={(e) => handleCantidadChange(index, e.target.value)}
                        disabled={row.cant_pendiente === 0}
                        fullWidth
                      />
                    </TableCell>

                    <TableCell align="center">
                      {/* BOTÓN DE LOTES */}
                      {row.artlote !== 0 && (
                        <Tooltip title="Asignar Lotes">
                          <span>
                            <IconButton
                              onClick={() => handleOpenModal(index, "LOTES")}
                              disabled={row.cant_pendiente === 0 || parseFloat(row.cant_afectar) <= 0}
                              sx={{
                                color: parseFloat(row.cant_afectar) > 0 && !lotesCompletos ? "#d32f2f" : "#2E7D32",
                              }}
                            >
                              <ViewListIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}

                      {/* BOTÓN DE SERIES */}
                      {row.artserie !== 0 && (
                        <Tooltip title="Asignar Series">
                          <span>
                            <IconButton
                              onClick={() => handleOpenModal(index, "SERIES")}
                              disabled={row.cant_pendiente === 0 || parseFloat(row.cant_afectar) <= 0}
                              sx={{
                                color: parseFloat(row.cant_afectar) > 0 && !seriesCompletas ? "#d32f2f" : "#2E7D32",
                              }}
                            >
                              <FormatListNumberedIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* =========================================================
          RENDERIZADO CONDICIONAL DE MODALES (INGRESO / EGRESO)
      ========================================================= */}

      {modalType === "LOTES" && tratipo === "INGRESO" && (
        <ModalLotesIngreso
          open={true}
          onClose={handleCloseModal}
          onSave={handleSaveLotes}
          articulo={currentRow}
          bodcodigo={bodcodigo}
        />
      )}

      {modalType === "LOTES" && tratipo === "EGRESO" && (
        <ModalLotesEgreso
          open={true}
          onClose={handleCloseModal}
          onSave={handleSaveLotes}
          articulo={currentRow}
          bodcodigo={bodcodigo}
        />
      )}

      {modalType === "SERIES" && tratipo === "INGRESO" && (
        <ModalSeriesIngreso
          open={true}
          onClose={handleCloseModal}
          onSave={handleSaveSeries}
          articulo={currentRow}
          bodcodigo={bodcodigo}
        />
      )}

      {modalType === "SERIES" && tratipo === "EGRESO" && (
        <ModalSeriesEgreso
          open={true}
          onClose={handleCloseModal}
          onSave={handleSaveSeries}
          articulo={currentRow}
          bodcodigo={bodcodigo}
        />
      )}
    </Box>
  )
}

export default DetalleArticulosInvGrid
